import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, XCircle, Loader2, Shield } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import { API_BASE } from '../config/api';

const API_URL = API_BASE;

export default function SSOCallbackPage() {
  const [status, setStatus] = useState('processing'); // processing, success, error
  const [message, setMessage] = useState('Completing authentication...');
  const [errorDetails, setErrorDetails] = useState('');
  
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setAuthData } = useAuth();
  
  useEffect(() => {
    // The Entra code-for-token exchange happens SERVER-SIDE, at
    // GET /api/sso/callback/azure. That handler finishes the exchange, creates
    // the session and redirects to the app with the token in the URL fragment,
    // where App.js hands it to AuthCallback.
    //
    // This page therefore only sees traffic when something is misconfigured -
    // most often a redirect URI registered as /sso/callback instead of
    // /api/sso/callback/azure. Say so plainly rather than attempting a
    // client-side exchange against an endpoint that no longer exists.
    const error = searchParams.get('error');
    const errorDescription = searchParams.get('error_description');
    const code = searchParams.get('code');

    if (error) {
      setStatus('error');
      setMessage('Authentication failed');
      setErrorDetails(errorDescription || error);
      return;
    }

    if (code) {
      setStatus('error');
      setMessage('Sign-in could not be completed');
      setErrorDetails(
        'This application received the sign-in response at the wrong address. ' +
        'The redirect URI registered in your directory should end with ' +
        '/api/sso/callback/azure. Please ask your administrator to correct it.'
      );
      return;
    }

    // Nothing actionable here - send the user back to sign in.
    navigate('/login', { replace: true });
  }, [searchParams, navigate]);

  return (
    <div className="min-h-screen bg-navigate-dark flex items-center justify-center px-4" data-testid="sso-callback-page">
      <div className="w-full max-w-md text-center">
        {/* Status Icon */}
        <div className="mb-6">
          {status === 'processing' && (
            <div className="inline-flex items-center justify-center w-20 h-20 bg-blue-600/20 rounded-full">
              <Loader2 className="h-10 w-10 text-blue-400 animate-spin" />
            </div>
          )}
          {status === 'success' && (
            <div className="inline-flex items-center justify-center w-20 h-20 bg-green-600/20 rounded-full">
              <CheckCircle className="h-10 w-10 text-green-400" />
            </div>
          )}
          {status === 'error' && (
            <div className="inline-flex items-center justify-center w-20 h-20 bg-red-600/20 rounded-full">
              <XCircle className="h-10 w-10 text-red-400" />
            </div>
          )}
        </div>
        
        {/* Status Message */}
        <h1 className="text-2xl font-bold text-white mb-2">{message}</h1>
        
        {/* Error Details */}
        {status === 'error' && errorDetails && (
          <p className="text-sm text-red-400 mb-6">{errorDetails}</p>
        )}
        
        {/* Processing Message */}
        {status === 'processing' && (
          <p className="text-zinc-400 mb-6">
            Please wait while we complete your sign-in with Microsoft Entra ID...
          </p>
        )}
        
        {/* Success Message */}
        {status === 'success' && (
          <p className="text-zinc-400 mb-6">
            Redirecting to your dashboard...
          </p>
        )}
        
        {/* Error Actions */}
        {status === 'error' && (
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={() => navigate('/enterprise-sso')}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
            >
              Try Again
            </button>
            <button
              onClick={() => navigate('/login')}
              className="px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-white rounded-lg transition-colors"
            >
              Standard Login
            </button>
          </div>
        )}
        
        {/* Security Badge */}
        <div className="mt-8 inline-flex items-center gap-2 text-xs text-zinc-500">
          <Shield className="h-4 w-4" />
          <span>Secured by Microsoft Entra ID</span>
        </div>
      </div>
    </div>
  );
}
